<template>
    <div>
    <ValidationProvider
        tag="div"
        :rules="selectRules"
        :name="vid"
        :label="label || name"
        :mode="mode"
        :model-value="modelValue"
        :validate-on-mount="false"
        :validate-on-input="false"
        :validate-on-model-update="false"
        v-slot="{ errors, validate }"
    >
        <!-- vee-validate was grabbing the first element with a v-model
        to apply validation on.  I added a v-model to the b-dropdown
    to avoid aplying validation to the search input.-->
        <b-form-group :label-for="name || labelText" :label="labelText" class="keep-on-top">
            <template #label>
                <span v-html="labelText"></span>
            </template>
            <b-row>
                <b-col class="">
                    <div :class="{ 'validation-fail': errors.length > 0 }">
                    <b-dropdown
                        ref="bDropDown"
                        variant="outline-primary"
                        :name="name"
                        :toggle-class="['select-button', 'custom-select', errors.length > 0 ? 'border-validation-fail' : '']"
                        :class="{
                            disabled: disabled,
                            'short-dropdown': shortDropdown,
                            'compact-format': compactFormat
                        }"
                        :disabled="disabled"
                        boundary="viewport"
                        strategy="fixed"
                        menu-class="p-advanced-select-menu"
                        :teleport-to="teleportTarget"
                        @shown="onMenuShown"
                        block
                    >
                        <template v-slot:button-content class="text-start">
                            <div class="text-start buttontext">
                                {{ buttonText }}
                                <br />
                            </div>
                        </template>
                        <b-dropdown-form v-show="!hideSearch" @click.stop>
                            <b-input
                                ref="searchInput"
                                type="search"
                                @update:model-value="debounceUserFilter"
                                placeholder="Search"
                                @keydown.down.prevent="focusResults"
                                @keydown.enter.prevent="filterEnter(validate)"
                            ></b-input>
                        </b-dropdown-form>
                        <div v-if="$slots.headerSlot" class="px-4" @click.stop>
                             <slot   name="headerSlot">
                            </slot>
                            <b-dropdown-divider></b-dropdown-divider>
                            </div>
                        <b-dropdown-item-button
                            v-if="
                                noOptionsMessage &&
                                    filteredOptions &&
                                    filteredOptions.length == 0
                            "
                            disabled
                        >
                            {{ noOptionsMessage }}
                        </b-dropdown-item-button>
                        <b-dropdown-item-button
                            v-for="(option, index) in userFilteredOptions.filter(x => !x.hidden)"
                            :active="isOptionSelected(option)"
                            :key="index"
                            :ref="`item${index}`"
                            :value="value"
                            :disabled="option.disabled"
                            @click="selectOption(option, validate)"
                        >
                            <slot name="optionText" :option="option">{{ option[itemText] }}</slot>

                        </b-dropdown-item-button>
                    </b-dropdown>
                    </div>
                </b-col>
                <b-col v-if="$slots.append" cols="auto" class="ms-auto ps-0">
                    <slot name="append"> </slot>
                </b-col>
            </b-row>
            <slot name="error-message" v-if="errors && errors.length > 0">
            <!-- allow for easy way to set a custom error message -->
            <span class="text-danger">{{ errors[0] }}</span>
            </slot>
        </b-form-group>
    </ValidationProvider>
    </div>
</template>

<script>
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import baseInputs from '@/components/mixins/BaseInputs';
import validatedInput from '@/components/mixins/ValidatedInput';
import { textIncludes } from '@/components/Common/Formatters.js';
import { useSelectListsStore } from '@/stores/selectLists.js';
import debounce from 'lodash/debounce';

export default {
    name: 'p-advanced-select',
    mixins: [baseInputs, validatedInput],
    emits: ['change', 'selectedObject', 'update:modelValue', 'input'],
    props: {
        // value prop now comes from modelValue via BaseInputs mixin
        dataType: String,
        options: Array,
        filter: [String, Number, Function],
        addEmptyOption: Boolean,
        addInvalidOption: {
            type: Object,
            default: null
        },
        emptyOnNullFilter: {
            type: Boolean,
            default: false
        },
        // For use with the filter function, will be passed as the second argument from the calling Vue template (possible values - value, item, index, field, isSelected, isExpanded)
        additionalFilterParameter: {
            type: Object,
            default: null
        },        
        itemText: {
            type: String,
            default: 'text'
        },
        itemValue: {
            type: String,
            default: 'value'
        },
        emptyValueSpace: {
            type: Boolean,
            default: false
        },
        returnObject: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        shortDropdown: {
            type: Boolean,
            default: false
        },
        hideSearch: {
            type: Boolean,
            default: false
        },
        noOptionsMessage: {
            type: String,
            default: ''
        },
        defaultToFirst:{
            type: Boolean,
            default: false
        },
        firstLoad:{
            type: Boolean,
            default: false
        },
        compactFormat: {
            type: Boolean,
            default: false
        },
        addDisabledText: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            innerOptions: [],
            selectedOption: null,
            userFilter: '',
            waitForInputDataTypes: ["customers"],
            lastClearedValue: undefined,
            // Default to body; overridden in mounted() if the component sits
            // inside a .modal so the teleported menu stays inside the modal's
            // focus trap (otherwise the dropdown's search input is unfocusable).
            teleportTarget: 'body'
        };
    },
    async created() {
        if (this.dataType) {
            // Triggers the fetch (which writes the result into the Pinia
            // store). Reactivity from Pinia state then keeps innerOptions
            // in sync via the pinedOptions watcher — no more manual
            // subscribe/unsubscribe to a global mutation bus.
            this.innerOptions = (await this.loadList(this.dataType)) || [];
        } else {
            this.innerOptions = this.options || [];
        }
        this.setInnerOptionsToObjects();

        if(this.defaultToFirst && this.value === null && this.filteredOptions.length > 0){
            this.selectOption(this.filteredOptions[0]);
        }

        if(this.firstLoad && this.value !== null && this.filteredOptions.length > 0){
            this.selectOption(this.getSelectedOption());
        }
    },
    mounted() {
        // Bootstrap modals trap focus inside their own DOM subtree. If the
        // dropdown menu teleports to <body> from inside a modal, the search
        // input inside the menu lands outside the focus trap and can't be
        // clicked or typed into. Teleporting to the modal element keeps it
        // inside the trap while still escaping the modal-dialog's transform/
        // overflow that would otherwise clip the menu.
        const modal = this.$el?.closest?.('.modal');
        if (modal) {
            this.teleportTarget = modal;
        }
    },
    beforeUnmount() {
        this.$refs.bDropDown?.hide?.(true);
    },
    computed: {
        value() {
            return this.modelValue;
        },
        buttonText() {
            if (!this.selectedOption) {
                // Default the unselected text to "Select a {Label}" when no
                // explicit message is supplied; fall back to a non-breaking
                // space (keeps button height) when there's no label either.
                return this.noSelectionMessage ?? (this.label ? `Select a ${this.label}` : '\xa0');
            }
            return this.selectedOption[this.itemText];
        },
        // Reactive pointer at the Pinia state for this dataType. When the
        // dataContext or SignalR replaces the array, this re-evaluates and
        // the watcher copies it onto innerOptions.
        pinedOptions() {
            if (!this.dataType) return null;
            return useSelectListsStore().lists[this.dataType];
        },

        //options that match the base filter ( doesn't apply the user filter)
        //these are all valid options
        filteredOptions() {
            let filteredOptions = this.innerOptions || [];
            if (this.filter || this.emptyOnNullFilter) {
                if (typeof this.filter == 'function') {
                    if(this.additionalFilterParameter)
                    {
                        filteredOptions = filteredOptions.filter(x =>
                            this.filter(x, this.additionalFilterParameter)
                        );
                    }
                    else
                    {
                        filteredOptions = filteredOptions.filter(x =>
                            this.filter(x)
                        );
                    }
                } else {
                    filteredOptions = filteredOptions.filter(
                        x => x.filter == this.filter
                    );
                }
            }
            if(this.addInvalidOption){
                filteredOptions = [this.addInvalidOption, ...filteredOptions]
            }
            
            if(this.innerOptions)
            {
                var disabledOptions = filteredOptions.filter(x => x.disabled === true);
                if(disabledOptions.length > 0)
                {
                    if(!disabledOptions.find(x => x[this.itemValue] === this.value))
                    {
                        filteredOptions = filteredOptions.filter(x => !x.disabled);
                    }else{
                        filteredOptions = filteredOptions.filter(x => !x.disabled || (x.disabled == true && x[this.itemValue] === this.value));
                    }                    
                }
            }

            if (this.addEmptyOption) {
                let emptyOption = {};
                // Show the "Select a {label}" prompt as the blank option's text;
                // fall back to a non-breaking space (keeps row height) when unlabeled.
                emptyOption[this.itemText] = this.selectPrompt ?? '\xa0';
                emptyOption[this.itemValue] = this.emptyValueSpace ? '\xa0' : null;
                return [emptyOption, ...filteredOptions];
            }
            return filteredOptions;
        },

        //options that match the base filter and then the user filter
        //these are the options displayed to the user to select.
        userFilteredOptions() {
            let filteredOptions = this.filteredOptions;

            if(this.waitForInputDataTypes.includes(this.dataType) && !this.userFilter)
            {
                return [];
            }

            if (this.userFilter != '') {
                filteredOptions = filteredOptions.filter(x =>
                    textIncludes(x[this.itemText], this.userFilter)
                );
            }
            
            return filteredOptions;
        }
    },
    watch: {
        options: {
            handler: function() {
                this.innerOptions = this.options || [];
                this.setInnerOptionsToObjects();
            }
        },
        pinedOptions: {
            handler: function(newVal) {
                if (newVal && newVal.length > 0) {
                    this.innerOptions = newVal;
                    this.setInnerOptionsToObjects();
                }
            }
        },
        filteredOptions: {
            handler: function() {
                let selectedOption = this.getSelectedOption();
                if (!selectedOption) {
                    // Only clear once per invalid value. If the parent immediately
                    // pushes the same invalid value back (e.g. a sentinel like -1),
                    // we don't keep emitting input(null) — that would feed an infinite
                    // loop because filteredOptions itself depends on this.value.
                    if (this.lastClearedValue !== this.value) {
                        this.lastClearedValue = this.value;
                        this.handleInput(null);
                    }
                    } else {
                        this.lastClearedValue = undefined;
                        this.selectedOption = selectedOption;
                }
            }
        },
        value: {
            handler: function() {
                //catch any value changes made outside this component
                this.selectedOption = this.getSelectedOption();
            }
        },
        userFilter: {
            handler: function() {
                //When the drop down pops up and is filtered to a small result set, the remaining items hover
                //on an island without moving down to meet the input.  Triggering redraw to fix.
                //Not critical, but avoids the input becoming disconnected.
                var resizeEvent = window.document.createEvent('UIEvents');
                resizeEvent.initUIEvent('resize', true, false, window, 0);
                window.dispatchEvent(resizeEvent);
            }
        }
    },

    methods: {
        debounceUserFilter: debounce(function (value) {
            this.userFilter = value;
        }, 300),
        filterEnter(validate) {
            if (this.userFilteredOptions.length == 1) {
                this.selectOption(this.userFilteredOptions[0], validate);
                this.$refs.bDropDown.hide(true);
            }
        },
        focusResults() {
            this.$refs.bDropDown.focusNext(new Event('keydown'));
        },
        isOptionSelected(option) {
            if (this.value === null || this.value === undefined) return false;
            // returnObject mode stores the whole option as the model — match by reference.
            if (this.returnObject) {
                if (this.value === option) return true;
                const a = this.value?.[this.itemValue];
                const b = option?.[this.itemValue];
                // Guard: if neither object carries an itemValue field, undefined === undefined
                // would mark every option as selected.
                if (a === undefined || b === undefined) return false;
                return a === b;
            }
            // Otherwise the model is the option's value field; compare by that.
            // Use loose equality so number-vs-string ids (e.g. coming back from JSON) still match.
            return option[this.itemValue] == this.value;
        },
        getSelectedOption() {
            if (this.returnObject) {
                return this.value;
            }
            var found = this.filteredOptions.find(
                x => x[this.itemValue] == this.value
            );

            if(this.addDisabledText && found && found.disabled && !found.text.includes(' (Disabled)'))
            {
                found.text = found.text + ' (Disabled)'
            }
            return found;
        },
        selectOption(option, validate) {
            this.selectedOption = option;
            this.$emit('selectedObject', option);

            this.handleInput(this.returnObject ? option : option[this.itemValue]);
            this.$emit('change');

            // Re-validate on a real user selection so a required error clears
            // once a value is picked. nextTick lets the v-model round-trip first.
            // Programmatic calls (defaultToFirst / firstLoad on mount) pass no
            // validate, so they don't validate prematurely.
            if (validate) {
                this.$nextTick(() => validate());
            }
        },
        async loadList(dataType) {
            return await selectListOptionsDataContext.getStoreDropdownData(
                dataType
            );
        },

        setInnerOptionsToObjects() {
            if (!this.innerOptions) { this.innerOptions = []; }
            //to match bootstrap selects
            //If option array is a list of strings, it will be used for both the generated value and text fields.
            if (
                this.innerOptions.length > 0 &&
                typeof this.innerOptions[0] === 'string'
            ) {
                this.innerOptions = this.innerOptions.map(x => ({
                    text: x,
                    value: x
                }));
            }
        },
        focusSearchInput() {
            this.userFilter = '';
            this.$nextTick(() => {
                const ref = this.$refs.searchInput;
                if (!ref) return;
                const el = ref.$el instanceof HTMLInputElement ? ref.$el : ref.$el?.querySelector('input');
                el?.focus();
            });
        },
        onMenuShown() {
            // BVN's b-dropdown teleport-to="body" handles the DOM placement
            // (Vue-aware Teleport keeps refs/listeners bound correctly, unlike
            // a manual appendChild which silently breaks click delivery on the
            // dropdown items).
            this.focusSearchInput();
        }
    }
};

</script>
<style scoped lang="scss">
@import "@/styles/app/common/variables.scss";
:deep(.selected-option ) {
    background: #ED9337;
}
:deep(.selected-option:hover ) {
    background: #ED9337 !important;
}
:deep(.disabled ) {
    background: var(--m-surface-sunken, #edf1f2);
}

:deep(.short-dropdown .dropdown-menu ) {
    max-height: 200px;
}

:deep(.compact-format .buttontext ) { 
   font-size: 0.9rem; 
   position: relative;
   transform: translate(-5px, -5px);
}

:deep(.dropdown-menu ) {
    max-height: 50vh;
    overflow: auto;
}

:deep(.dropdown-menu.show ) {
    width: max-content;
}

:deep(.compact-format .dropdown-toggle ) { 
   max-height: 24px !important;
}

:deep(.dropdown-toggle ) {
    text-align: left;
}

:deep(.dropdown-toggle::after ) {
    //using the caret SVG provided by .custom-select override below, so hiding bvn's default
    display: none;
}

// Override .custom-select's default up/down double-chevron with Bootstrap 5's
// single down-caret SVG, so p-advanced-select matches p-select visually.
:deep(button.btn-outline-primary.select-button.custom-select ) {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right 0.75rem center !important;
    background-size: 16px 12px !important;
}

:deep(.buttontext ) {
    white-space: nowrap;
    overflow: hidden;
}
:deep(.btn.dropdown-toggle ) {
    border-radius: 0px;
}

// .border-validation-fail styling lives in bootstrap-reset.scss so it can
// match the high-specificity selector that sets the default border color.

:deep(.dropdown-item:hover ) {
    background-color: #ED9337 !important;
    color: white !important;
}

:deep(button.btn-outline-primary.select-button ) {
    display: inline-block;
    width: 100%;
    height: 2.1875rem;
    padding: 0.375rem 2rem 0.375rem 1rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.52857;
    color: var(--m-text, #495057);
    vertical-align: middle;
    border-radius: 0;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border: 1px solid $input-default-border-color !important;
    font-size: 1rem;
}
:deep(button.btn-outline-primary.select-button:disabled ) {
    border: 1px solid $input-default-border-color !important;
}
:deep(.btn-outline-primary.select-button[aria-expanded="true"] ) {
    /* Tokenised for consistency, but inert in dark: BootstrapVue puts .show on the toggle
       exactly when aria-expanded is true, and _dark.scss's
       `.btn-outline-primary:not(.active).show` sets both colour and background with
       !important, which this rule has none of. So the dark appearance of an open select
       comes from there, not here. Kept paired anyway so the two halves cannot drift if
       that !important is ever relaxed. Light mode byte-identical -- the fallback
       interpolates the same variable the line used before. */
    color: var(--m-link, #{$btn-secondary-color});
    background-color: var(--m-surface-sunken, #f5f5f5);
}
</style>

<style lang="scss">
.p-advanced-select-menu {
    /* strategy="fixed" positions the menu relative to the viewport.
       Bootstrap modal sits at z-index 1055; topnavbar at 1050. Use a
       deliberately high value to stay above both. */
    z-index: 99999 !important;
    /* The scoped :deep(.dropdown-menu) rules below don't apply once the menu
       is moved to <body> (scoping attribute is no longer on any ancestor).
       Without these, the menu has no internal scroll, so scrolling the page
       lets Popper reposition mid-click — the cursor's target shifts and the
       click lands on the <ul> instead of the dropdown-item, closing without
       selecting. */
    max-height: 50vh;
    overflow: auto;
}
/* The dropdown menu is positioned with strategy="fixed" / teleported, so the
   component's scoped :deep() rules don't reach it. Use bvn's built-in `active`
   prop (which adds .active to the visible .dropdown-item button) and override
   its color with the project's orange. */
.p-advanced-select-menu .dropdown-item.active,
.p-advanced-select-menu .dropdown-item.active:hover,
.p-advanced-select-menu .dropdown-item.active:focus {
    background-color: #ED9337 !important;
    color: white !important;
}
</style>
